import '@ory/elements-react/theme/styles.css';
import '@apps/web/global.css';

import * as Sentry from '@sentry/react';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { Suspense, useEffect } from 'react';
import ReactDOM from 'react-dom/client';

import { AppNotificationBridge } from '@apps/web/components/app_notification_bridge';
import { AppSessionLoading } from '@apps/web/components/app_session_loading';
import { ThemePolicy } from '@apps/web/components/layout/theme_policy';
import { InCallPanel } from '@apps/web/components/voice_calls/in_call_panel';
import { CallProvider } from '@apps/web/context/call_provider';
import { IntercomProvider } from '@apps/web/context/intercom_provider';
import { OrganizationProvider } from '@apps/web/context/organization_provider';
import {
    type SessionStateContextType,
    SessionStateProvider,
    useSessionState,
} from '@apps/web/context/session_state_provider';
import { SWRProvider } from '@apps/web/context/swr_provider';
import { OrganizationTimezoneProvider } from '@apps/web/context/timezone_provider';
import { UIProvider } from '@apps/web/context/ui_provider';
import { routeTree } from '@apps/web/generated_route_tree';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { initializeDatadogLogs } from '@apps/web/lib/datadog_logs';
import { initializeDatadogRum } from '@apps/web/lib/datadog_rum';
import {
    isOrganizationEventTestEnvironment,
    organizationEventBus,
} from '@apps/web/lib/event_bus';
import { lazyAnalytics } from '@apps/web/lib/lazy_analytics';
import { clearReleaseReloadMarker } from '@apps/web/lib/release_update_store';
import { initializeRuntimeConfig } from '@apps/web/lib/runtime_config';
import {
    scrubErrorEventUrls,
    scrubTransactionEventUrls,
    shouldDropSentryError,
} from '@apps/web/lib/sentry_tracing';
import { captureTeamsConnection } from '@apps/web/lib/teams_connection';
import {
    useUser,
    useUserProfile,
} from '@apps/web/swr_data_fetching/current_user_queries';
import { type User } from '@libraries/typescript/api_client';
import { assert } from '@libraries/typescript/utilities';

captureTeamsConnection();
clearReleaseReloadMarker();

const router = createRouter({
    routeTree,
    context: { sessionState: null, user: null },
});

type AppRouter = typeof router;

const PostHogInitializer = lazyAnalytics(() =>
    import('@apps/web/context/posthog_provider').then((module) => ({
        default: module.PostHogInitializer,
    })),
);
const PostHogIdentify = lazyAnalytics(() =>
    import('@apps/web/context/posthog_provider').then((module) => ({
        default: module.PostHogIdentify,
    })),
);

declare module '@tanstack/react-router' {
    // oxlint-disable-next-line @typescript-eslint/consistent-type-definitions
    interface Register {
        readonly router: AppRouter;
    }
}

async function bootstrap() {
    const runtimeConfig = await initializeRuntimeConfig();
    const appRouter = runtimeConfig.basePath
        ? createRouter({
              routeTree,
              basepath: runtimeConfig.basePath,
              context: { sessionState: null, user: null },
          })
        : router;
    if (
        isOrganizationEventTestEnvironment(
            window.location.hostname,
            runtimeConfig.runEnvironment === 'development',
        )
    ) {
        (window as unknown as Record<string, unknown>)[
            '__phoebeOrganizationEventBusForTests__'
        ] = organizationEventBus;
    }
    void initializeDatadogLogs(runtimeConfig).catch((error) => {
        console.error('Datadog Logs initialization failed', error);
    });
    void initializeDatadogRum(runtimeConfig).catch((error) => {
        console.error('Datadog RUM initialization failed', error);
    });

    // Agentation's feedback mode overlays the whole page and intercepts
    // clicks, which breaks normal text selection if it gets toggled on by
    // accident. Require an explicit opt-in via localStorage so devs who use
    // it can enable it, but it stays off for everyone else.
    const AgentationComponent =
        runtimeConfig.runEnvironment === 'development' &&
        runtimeConfig.agentationEndpoint &&
        localStorage.getItem('agentation') === 'on'
            ? (await import('agentation')).Agentation
            : null;

    Sentry.init({
        dsn: runtimeConfig.sentryDsn ?? undefined,
        dataCollection: { userInfo: true },
        // Browser tracing records pageload/navigation transactions, fetch/XHR
        // timings, and Web Vitals for client-observed latency. We leave
        // `tracePropagationTargets` at its default so Sentry does not inject
        // `sentry-trace`/`baggage` headers into cross-origin api.phoebe.work
        // requests (which would require a CORS allowlist change).
        integrations: [Sentry.browserTracingIntegration()],
        // 100% sampling because the APAC sessions we care about are rare;
        // `tracesSampleRate` is the main cost lever and can be lowered later.
        tracesSampleRate: 1.0,
        // Strip query strings from transaction/request/span URLs so
        // user-entered search terms (e.g. a client name) never reach Sentry;
        // the endpoint path is all we need for per-endpoint latency.
        beforeSend: (event) =>
            shouldDropSentryError(event) ? null : scrubErrorEventUrls(event),
        beforeSendTransaction: scrubTransactionEventUrls,
        environment: runtimeConfig.runEnvironment,
        release: runtimeConfig.sha,
    });

    const rootElement = document.getElementById('root');
    assert.isNotNull(rootElement);

    const root = ReactDOM.createRoot(rootElement, {
        onUncaughtError: Sentry.reactErrorHandler(),
        onCaughtError: Sentry.reactErrorHandler(),
        onRecoverableError: Sentry.reactErrorHandler(),
    });

    root.render(
        <SessionStateProvider>
            <SWRProvider>
                <UIProvider>
                    <Suspense fallback={null}>
                        <PostHogInitializer />
                    </Suspense>
                    <App router={appRouter} />
                    {AgentationComponent && (
                        <AgentationComponent
                            endpoint={
                                runtimeConfig.agentationEndpoint ?? undefined
                            }
                            onSessionCreated={() => {}}
                        />
                    )}
                </UIProvider>
            </SWRProvider>
        </SessionStateProvider>,
    );
}

bootstrap().catch((e) => console.error(e));

function App({ router }: { readonly router: AppRouter }) {
    const sessionState = useSessionState();

    if (!sessionState.initialized) {
        return <AppSessionLoading />;
    }

    if (!sessionState.session) {
        return (
            <RouterProvider
                router={router}
                context={{ sessionState, user: null }}
            />
        );
    }

    return <AuthenticatedApp router={router} sessionState={sessionState} />;
}

function AuthenticatedApp({
    router,
    sessionState,
}: {
    router: AppRouter;
    sessionState: SessionStateContextType;
}) {
    const { data: user, isLoading } = useUser({ reason: 'main' });
    // OrganizationProvider mounts only after the user loads. Request its
    // profile now so the two requests run in parallel, not back to back.
    useUserProfile({ reason: 'organization-provider' });

    // The router loads the landing page's code only when it renders, after
    // the user and organization requests. The code needs neither, so load it
    // alongside them.
    useEffect(() => {
        const [matchedRoutes] = router.getMatchedRoutes(
            router.state.location.pathname,
        );
        for (const route of matchedRoutes) {
            // The router reports a failed load when it renders the page.
            router.loadRouteChunk(route)?.catch(() => {});
        }
    }, [router]);

    if (isLoading) {
        return <AppSessionLoading />;
    }

    return (
        <OrganizationProvider>
            <ThemePolicy />
            <OrganizationTimezoneProvider>
                <IntercomProvider>
                    <CallProvider>
                        <AppNotificationBridge />
                        <Suspense fallback={null}>
                            <PostHogIdentify user={user} />
                        </Suspense>
                        <SentryIdentify user={user} />
                        <RouterProvider
                            router={router}
                            context={{ sessionState, user }}
                        />
                        <InCallPanel />
                    </CallProvider>
                </IntercomProvider>
            </OrganizationTimezoneProvider>
        </OrganizationProvider>
    );
}

// Attach the authenticated user and their current organization to every
// Sentry event so triage can filter/group by org and jump to the affected
// user. Without this, exceptions captured by `@sentry/react` have no link
// back to who or what org they affected.
function SentryIdentify({ user }: { user?: User }) {
    const organizationId = useOrganizationId();

    useEffect(() => {
        if (!user) {
            Sentry.setUser(null);
            Sentry.setTag('organization_id', undefined);
            return;
        }
        Sentry.setUser({ id: user.id, email: user.email ?? undefined });
        Sentry.setTag('organization_id', organizationId);
        // On logout, `AuthenticatedApp` unmounts rather than re-rendering with
        // `user=undefined`, so without this cleanup the last user/org would
        // stay attached to Sentry's global scope and be misattributed to any
        // exception raised on the unauthenticated login screen.
        return () => {
            Sentry.setUser(null);
            Sentry.setTag('organization_id', undefined);
        };
    }, [user, organizationId]);

    return null;
}
